import { useEffect, useState, useCallback, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Download, ZoomIn, ZoomOut, RotateCw, Play, Pause } from 'lucide-react';

/**
 * Premium image lightbox with:
 *  - Aggressive prefetch of next/prev images (Google Drive feel)
 *  - Keyboard navigation (← → Esc)
 *  - Pinch/wheel zoom + rotate
 *  - Loading skeleton until image is decoded
 *  - Uses immutable HTTP cache (max-age 1 year) so 2nd open is instant
 *
 * Props:
 *  - photos: array of { id, src, fullSrc, name, lat?, lng?, alt?, uploaded_by?, created_at? }
 *      `src` is the THUMBNAIL url (used for instant preview); `fullSrc` is the full-size url.
 *  - initialIndex: which photo to show first
 *  - onClose: () => void
 */
export default function PhotoLightbox({ photos, initialIndex = 0, onClose }) {
  const [index, setIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [slideshow, setSlideshow] = useState(false);
  const slideshowIntervalMs = 4000;
  const slideTimerRef = useRef(null);

  const cur = photos[index];
  const nextIdx = (index + 1) % photos.length;
  const prevIdx = (index - 1 + photos.length) % photos.length;

  // Reset zoom/rotation when navigating
  useEffect(() => {
    setZoom(1);
    setRotation(0);
    setLoaded(false);
  }, [index]);

  // Prefetch neighbors silently — makes navigation feel instant
  useEffect(() => {
    if (!cur) return;
    const prefetch = (url) => {
      if (!url) return;
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
    };
    prefetch(photos[nextIdx]?.fullSrc);
    prefetch(photos[prevIdx]?.fullSrc);
  }, [index, photos, cur, nextIdx, prevIdx]);

  // Keyboard navigation
  const navigate = useCallback((delta) => {
    setIndex((i) => (i + delta + photos.length) % photos.length);
  }, [photos.length]);

  // Slideshow auto-advance
  useEffect(() => {
    if (slideshow && photos.length > 1) {
      slideTimerRef.current = setTimeout(() => {
        navigate(1);
      }, slideshowIntervalMs);
    }
    return () => {
      if (slideTimerRef.current) clearTimeout(slideTimerRef.current);
    };
  }, [slideshow, index, navigate, photos.length]);

  // Stop slideshow on close
  useEffect(() => () => setSlideshow(false), []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowRight') navigate(1);
      else if (e.key === 'ArrowLeft') navigate(-1);
      else if (e.key === '+' || e.key === '=') setZoom((z) => Math.min(5, z * 1.25));
      else if (e.key === '-') setZoom((z) => Math.max(0.25, z / 1.25));
      else if (e.key === 'r' || e.key === 'R') setRotation((r) => (r + 90) % 360);
      else if (e.key === '0') { setZoom(1); setRotation(0); }
      else if (e.key === 'p' || e.key === 'P' || e.key === ' ') {
        e.preventDefault();
        setSlideshow((s) => !s);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, navigate]);

  if (!cur) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/95 backdrop-blur-md flex flex-col"
      data-testid="photo-lightbox"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      {/* Slideshow progress bar */}
      {slideshow && photos.length > 1 && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/10 z-30" data-testid="slideshow-progress">
          <div
            key={index}
            className="h-full bg-cyan-400"
            style={{ animation: `slideshowProgress ${slideshowIntervalMs}ms linear` }}
          />
        </div>
      )}
      <style>{`
        @keyframes slideshowProgress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
      {/* Top bar */}
      <div className="flex items-center justify-between p-3 sm:p-4 text-white">
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate" data-testid="lightbox-filename">
            {cur.name}
          </div>
          <div className="text-[11px] text-gray-400 font-mono truncate">
            {photos.length > 1 && <span>{index + 1} / {photos.length} · </span>}
            {cur.lat != null && cur.lng != null && (
              <span>{cur.lat.toFixed(6)}, {cur.lng.toFixed(6)}{cur.alt != null && ` · alt ${cur.alt}m`}</span>
            )}
            {cur.uploaded_by && <span> · {cur.uploaded_by}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {photos.length > 1 && (
            <button
              type="button"
              onClick={() => setSlideshow((s) => !s)}
              title={slideshow ? 'Pausar slideshow (P)' : 'Iniciar slideshow (P)'}
              className={`flex items-center justify-center w-10 h-10 rounded-lg transition ${slideshow ? 'bg-cyan-500 text-white' : 'hover:bg-white/10 text-white'}`}
              data-testid="lightbox-slideshow-toggle"
            >
              {slideshow ? <Pause size={18} /> : <Play size={18} />}
            </button>
          )}
          <IconBtn onClick={() => setZoom((z) => Math.max(0.25, z / 1.25))} title="Diminuir (-)"><ZoomOut size={18} /></IconBtn>
          <span className="text-xs font-mono text-gray-300 w-12 text-center" data-testid="lightbox-zoom">{Math.round(zoom * 100)}%</span>
          <IconBtn onClick={() => setZoom((z) => Math.min(5, z * 1.25))} title="Aumentar (+)"><ZoomIn size={18} /></IconBtn>
          <IconBtn onClick={() => setRotation((r) => (r + 90) % 360)} title="Rotacionar (R)"><RotateCw size={18} /></IconBtn>
          {cur.fullSrc && (
            <a
              href={cur.fullSrc} download={cur.name}
              className="flex items-center justify-center w-10 h-10 hover:bg-white/10 rounded-lg transition"
              title="Baixar"
              data-testid="lightbox-download"
              onClick={(e) => e.stopPropagation()}
            >
              <Download size={18} />
            </a>
          )}
          <IconBtn onClick={onClose} title="Fechar (Esc)" data-testid="lightbox-close">
            <X size={20} />
          </IconBtn>
        </div>
      </div>

      {/* Image area */}
      <div
        className="flex-1 flex items-center justify-center overflow-hidden p-2 sm:p-6 select-none"
        onWheel={(e) => {
          e.preventDefault();
          const delta = e.deltaY > 0 ? 0.9 : 1.1;
          setZoom((z) => Math.min(5, Math.max(0.25, z * delta)));
        }}
      >
        {!loaded && (
          <div className="absolute w-12 h-12 border-2 border-cyan-400/40 border-t-cyan-400 rounded-full animate-spin" />
        )}
        <img
          key={cur.id}
          src={cur.fullSrc || cur.src}
          alt={cur.name}
          decoding="async"
          onLoad={() => setLoaded(true)}
          onClick={(e) => e.stopPropagation()}
          className="max-w-full max-h-full object-contain transition-transform duration-150 will-change-transform"
          style={{
            transform: `scale(${zoom}) rotate(${rotation}deg)`,
            cursor: zoom > 1 ? 'grab' : 'default',
            opacity: loaded ? 1 : 0.3,
          }}
        />
      </div>

      {/* Nav buttons */}
      {photos.length > 1 && (
        <>
          <button
            onClick={() => navigate(-1)}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white transition flex items-center justify-center"
            title="Anterior (←)"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            onClick={() => navigate(1)}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white transition flex items-center justify-center"
            title="Próxima (→)"
            data-testid="lightbox-next"
          >
            <ChevronRight size={22} />
          </button>
        </>
      )}
    </div>
  );
}

function IconBtn({ onClick, title, children, ...rest }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="flex items-center justify-center w-10 h-10 hover:bg-white/10 rounded-lg transition text-white"
      {...rest}
    >
      {children}
    </button>
  );
}
